import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Container, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as _ from 'lodash';
import { setSelectedMatch, fetchFullEvent, fetchBetBuilderAvailableSelections } from 'stores/match/index.js';
import { BetGroups, getBetGroups } from 'toolkit/constants.js';
import { setBetBuilderEnabled, setBetBuilderModalShow, calculateBetBuilderOdds, clearBetBuilderSelections } from 'stores/betSlip';
import { showToaster } from 'mixin/general';
import {
  MarketIdToMarketName,
  ReverseName,
  checkHasMarketVariable,
  getLanguageCode,
  gameDateToMatchDayFormatted,
  sportTypeToSportId
} from 'toolkit/utils';
import { AWS_BUCKET_URL, BET_BUILDER_ENABLED } from 'toolkit/constants';
import { getMatchTime } from 'toolkit/utils.js';
import { EnumMarketName, EnumEventStatus } from 'toolkit/Enums.js';
import { getMatchDetail, getTeamNameHelper } from 'helper/matchDetailHelper';
import MatchTracker from '../match_tracker/match_tracker.js';
import MatchStreamer from '../match_tracker/match_streamer.js';
import Statistics from '../match_tracker/statistics/statistics.js';
import BetBox from './bet_box';
import GoBack from './go_back';
import BetTitleAndBox from './bet_title_and_box.js';
import BetBuilderModal from './bet_builder_modal.js';
import { isMarketLineOfferedForBetBuilder } from 'toolkit/betBuilder';
import { EBetGroupName } from 'toolkit/Enums.js';
import { EnumMatchStatusName } from 'toolkit/Enums.js';
import Jersey from 'components/jersey/index.js';

function MatchDetail({ matchDetailId, onMatchDetailIdSet }) {
  const dispatch = useDispatch();
  const { t } = useTranslation('translations', { useSuspense: false });
  const location = useLocation();
  const navigate = useNavigate();
  const providerId = matchDetailId || location.hash.slice(1);
  const match = useSelector((state) => state.match.events[providerId]);
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const locales = useSelector((state) => state.match.locales);
  const [matchDetail, setMatchDetail] = useState(undefined);
  const [availableMarketGroups, setAvailableMarketGroups] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(EBetGroupName.TOP_BETS);
  const [matchtrackerCallback, setMatchtrackerCallback] = useState(null);
  const [isStreamView, setIsStreamView] = useState(false);
  const [currentView, setCurrentView] = useState('tracker');
  const [streamError, setStreamError] = useState(false);
  const [streamAvailable, setStreamAvailable] = useState(false);
  const [streamUrl, setStreamUrl] = useState('');
  const [fullscreenUrl, setFullscreenUrl] = useState('');
  const [trackerTab, setTrackerTab] = useState('tracker');
  const hasAutoScrolledFromBetSlip = useRef(false);
  const betBuilder = useSelector((state) => state.betSlip.betBuilder);
  const betSlip = useSelector((state) => state.betSlip);
  const betBuilderAvailableSelections = useSelector((state) => state.match.betBuilderAvailableSelections[providerId]);

  // Check if bet builder is available for this match
  const isBetBuilderAvailable = () => {
    // Check global feature flag first
    if (!BET_BUILDER_ENABLED) return false;

    if (!betBuilderAvailableSelections) return false;

    // Check if the response has the expected structure with success and data
    if (betBuilderAvailableSelections.success && betBuilderAvailableSelections.data) {
      const { markets } = betBuilderAvailableSelections.data;
      return Array.isArray(markets) && markets.length > 0;
    }

    return false;
  };

  // Get available market IDs for bet builder
  const getBetBuilderAvailableMarketIds = () => {
    if (!isBetBuilderAvailable()) return [];
    
    const { markets } = betBuilderAvailableSelections.data;
    return markets.map(market => market.marketId);
  };

  // Check if a specific market is available for bet builder
  const isMarketAvailableForBetBuilder = (marketId) => {
    if (!isBetBuilderAvailable()) return false;
    const availableMarketIds = getBetBuilderAvailableMarketIds();
    return availableMarketIds.includes(marketId);
  };

  // Bet Builder Icon Component
  const BetBuilderIcon = ({ size = 24, style = {}, firstBColor = '#000000' }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      style={{ marginLeft: '6px', verticalAlign: 'middle', cursor: 'pointer', ...style }}
      title={t('availableForBetBuilder')}
    >
      <g transform="skewX(-14)">
        {/* First B (configurable color) */}
        <text
          x="6"
          y="46"
          fontSize="42"
          fontWeight="900"
          fontFamily="Arial Black, Arial, sans-serif"
          fill={firstBColor}
          letterSpacing="-5"
        >
          B
        </text>
        {/* Red B */}
        <text
          x="30"
          y="46"
          fontSize="42"
          fontWeight="900"
          fontFamily="Arial Black, Arial, sans-serif"
          fill="#E10600"
          letterSpacing="-5"
        >
          B
        </text>
      </g>
    </svg>
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMatch();

    return () => {
      dispatch(setBetBuilderEnabled(false));
      dispatch(clearBetBuilderSelections());
    };
  }, []);

  // Reset match detail to initial state when bet builder is disabled
  useEffect(() => {
    if (!betBuilder.isEnabled) {
      setSelectedMarket(EBetGroupName.TOP_BETS);
      setCurrentView('tracker');
      setIsStreamView(false);
      setStreamError(false);
    }
  }, [betBuilder.isEnabled]);

  function fetchMatch() {
    dispatch(setSelectedMatch(providerId));
    dispatch(fetchFullEvent(providerId))
      .unwrap()
      .then((response) => {
        if (!response) {
          navigate('/live');
        }
      });

    // Fetch bet builder available selections for this match
    dispatch(fetchBetBuilderAvailableSelections(providerId));
  }

  useEffect(() => {
    if (match) {
      setMatchDetail(getMatchDetail(match));
      // Check if stream is available when match is loaded
      checkStreamAvailability();
    }
  }, [match]);

  function checkStreamAvailability() {
    if (!match || !match.providerId) return;

    const matchId = match.providerId.split(':')[2];
    const url = `https://geturl.yextof.com/bradar?bradar=${matchId}`;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          setStreamAvailable(false);
          setStreamUrl('');
          setFullscreenUrl('');
        } else if (data.fullscreen && typeof data.fullscreen === 'string' && data.fullscreen.length > 0) {
          setStreamAvailable(true);
          setFullscreenUrl(data.fullscreen);
          // Keep regular URL if available, otherwise clear it
          if (data.url && typeof data.url === 'string' && data.url.length > 0) {
            setStreamUrl(data.url);
          } else {
            setStreamUrl('');
          }
        } else if (data.url && typeof data.url === 'string' && data.url.length > 0) {
          setStreamAvailable(true);
          setStreamUrl(data.url);
          setFullscreenUrl('');
        } else {
          setStreamAvailable(false);
          setStreamUrl('');
          setFullscreenUrl('');
        }
      })
      .catch((error) => {
        console.error('Error checking stream availability:', error);
        setStreamAvailable(false);
        setStreamUrl('');
        setFullscreenUrl('');
      });
  }


  useEffect(() => {
    getMarketGroups();
  }, [match]);

  useEffect(() => {
    if (!location.state?.scrollToMarketHeader || hasAutoScrolledFromBetSlip.current) return;
    if (!match) return;

    const scrollTimer = setTimeout(() => {
      const marketHeader = document.querySelector('.match-details .d-flex.justify-content-between.mb-2');
      if (marketHeader) {
        marketHeader.scrollIntoView({ behavior: 'smooth', block: 'center' });
        hasAutoScrolledFromBetSlip.current = true;
      }
    }, 200);

    return () => clearTimeout(scrollTimer);
  }, [location.state, match, selectedMarket]);

  // Calculate bet builder odds when selections change; quick successive clicks send one request
  useEffect(() => {
    if (!betBuilder.isEnabled || betBuilder.selections.length === 0) return;
    const timer = setTimeout(() => dispatch(calculateBetBuilderOdds(betBuilder.selections)), 300);
    return () => clearTimeout(timer);
  }, [betBuilder.selections, betBuilder.isEnabled, dispatch]);

  let buttonBg = '';
  if (match?.eventStatus > EnumEventStatus.NOTSTARTED) {
    if (match.matchStatusId === EnumMatchStatusName.HALFTIME) {
      buttonBg = 'bg-button-ht';
    } else {
      buttonBg = 'bg-button-will-live';
    }
  } else {
    buttonBg = 'bg-button-will-no-live';
  }

  function getMarketGroups() {
    if (match === undefined) return;
    const tempAvailableTabOrders = new Map();
    match.markets.forEach((m) => {
      const betGroupNames = getBetGroups(m.id) ?? [EBetGroupName.SPECIAL_BETS];
      betGroupNames.map((betGroupName) => {
        if (!tempAvailableTabOrders.has(betGroupName)) {
          tempAvailableTabOrders.set(betGroupName, BetGroups[betGroupName].tabOrder);
        }
      });
    });

    setAvailableMarketGroups(
      Array.from(tempAvailableTabOrders.entries())
        .sort(([, tabOrderA], [, tabOrderB]) => tabOrderA - tabOrderB)
        .map(([name]) => {
          return name;
        })
    );

    if (tempAvailableTabOrders.length == 1) {
      setSelectedMarket(tempAvailableTabOrders[0]);
    }
  }

  function getGoalScorers(outcomeId, marketId) {
    const market = match.markets.find((x) => x.id === marketId);
    if (market) {
      return (
        <Col xs={2} className="m-0 p-1">
          <BetBox
            key={outcomeId}
            providerId={providerId}
            outcomeId={outcomeId}
            marketStringId={market.stringId}
            showOddTitle={false}
            betBox={buttonBg}
          />
        </Col>
      );
    } else {
      return (
        <Col xs={2} className="m-0 p-1">
          <BetBox
            key={outcomeId}
            providerId={providerId}
            outcomeId={outcomeId}
            marketStringId={null}
            showOddTitle={false}
            betBox={buttonBg}
          />
        </Col>
      );
    }
  }

  const sortByMarketId = (arr) => {
    return _.orderBy(arr, (market) => {
      const marketVariable = checkHasMarketVariable(market.id, market.stringId);
      if (marketVariable) {
        if (!isNaN(Number(marketVariable))) return Number(marketVariable);
        return marketVariable;
      }
      return false;
    });
  };

  const detailBorderClass =
    match?.matchStatusId === EnumMatchStatusName.HALFTIME
      ? 'bg-color-dark-blue'
      : match?.eventStatus === EnumEventStatus.RUNNING
        ? 'bg-color-dark-green'
        : 'bg-color-dark';
  function getMarkets(betGroupName) {
    let markets;
    
    // In bet builder mode show only the market lines MyOdds offers (e.g. Total 2.5, not every total line)
    const betBuilderOfferedMarkets =
      betBuilder.isEnabled && isBetBuilderAvailable() ? betBuilderAvailableSelections.data.markets : null;
    
    if (betGroupName === EBetGroupName.SPECIAL_BETS) {
      const includedBetId = Object.values(BetGroups).flatMap((group) => group.markets);
      const notIncludedBets = match.markets.filter((item) => !includedBetId.includes(item.id));
      markets = [
        ...match.markets
          .filter((item) => BetGroups[betGroupName].markets.includes(item.id))
          .sort((t1, t2) => {
            return (
              BetGroups[EBetGroupName.SPECIAL_BETS].markets.indexOf(t1.id) -
              BetGroups[EBetGroupName.SPECIAL_BETS].markets.indexOf(t2.id)
            );
          }),
        ...notIncludedBets
      ];
    } else {
      markets = [
        ...match.markets
          .filter((item) => BetGroups[betGroupName].markets.includes(item.id))
          .map((m) => ({ ...m, outcomes: [...m.outcomes] }))
      ];
    }
    
    // Apply bet builder filtering if enabled
    if (betBuilderOfferedMarkets) {
      markets = markets.filter((market) => isMarketLineOfferedForBetBuilder(market, betBuilderOfferedMarkets));
    }
    const marketList = [];

    // Top Bets Markets Groups
    if (betGroupName === EBetGroupName.TOP_BETS) {
      const correctScore = [];
      const overUnderTopBets = [];
      const handicapTopBets = [];
      const maradonaHandicapTopBets = [];
      const goalRangeTopBets = [];
      const winnerTopBets = [];
      const winnerEarlyPayoutBets = [];
      const nextGoalTopBets = [];
      const bothTeamToScoreTopBets = [];
      const otherList = [];
      markets = markets.sort((t1, t2) => t1.id - t2.id);
      detailBorderClass;

      const marketRom = markets.find((x) => x.id == EnumMarketName.ROM_7);
      if (marketRom) {
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-' + marketRom.stringId}>
            <BetTitleAndBox
              key={marketRom.stringId}
              market={marketRom}
              matchId={providerId}
              matchDetail={matchDetail}
              sportType={match.sport}
              betBox={buttonBg}
              betBuilderIcon={
                isMarketAvailableForBetBuilder(marketRom.id) ? (
                  <BetBuilderIcon firstBColor="#000000" />
                ) : null
              }
            />
          </div>
        );
      }
      if (match.sport == sportTypeToSportId('Football')) {
        markets = _.orderBy(markets, (market) => {
          if (market.id == EnumMarketName.TOTAL_18 || market.id == EnumMarketName.HANDICAP_14)
            return market.stringId.split(':')[1];
        });
        markets.forEach((market) => {
          if (market.id != EnumMarketName.ROM_7) {
            let targetArr;
            if (market.id == EnumMarketName.TOTAL_18) {
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              targetArr = overUnderTopBets;
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBox={buttonBg}
                />
              );
              if (targetArr.length == 0) {
                targetArr.push(
                  <div
                    className={`text-start fs-6 over-under d-flex align-items-center`}
                    key={'bet_name' + market.stringId + 'top'}>
                    {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                    {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon firstBColor="#000000" />}
                  </div>
                );
              }
              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.HANDICAP_14) {
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              const specifier = checkHasMarketVariable(market.id, market.stringId);
              const isMaradonaHandicap =
                match.eventStatus === EnumEventStatus.NOTSTARTED &&
                (specifier === '0:9' || specifier === '9:0');
              targetArr = isMaradonaHandicap ? maradonaHandicapTopBets : handicapTopBets;
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId + 'tophan'}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBox={buttonBg}
                />
              );
             
              if (targetArr.length == 0 && !isMaradonaHandicap) {
                targetArr.push(
                  <div className={`text-start fs-6 d-flex align-items-center`} key={'bet_name' + market.stringId + 'tophan'}>
                    {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                    {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon firstBColor="#000000" />}
                  </div>
                );
              }

              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.GOALRANGE_25) {
              targetArr = goalRangeTopBets;
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBox={buttonBg}
                />
              );
              if (targetArr.length == 0) {
                targetArr.push(
                  <div className={`text-start fs-6 d-flex align-items-center`} key={'bet_name' + market.stringId}>
                    {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                    {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon firstBColor="#000000" />}
                  </div>
                );
              }

              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.NEXTGOAL_8) {
              targetArr = nextGoalTopBets;
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBox={buttonBg}
                />
              );
              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.WINNER_1) {
              targetArr = winnerTopBets;
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBox={buttonBg}
                  betBuilderIcon={isMarketAvailableForBetBuilder(market.id) ? <BetBuilderIcon firstBColor="#000000" /> : null}
                />
              );
              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.WINNEREARLYPAYOUT_1601) {
              targetArr = winnerEarlyPayoutBets;
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBox={buttonBg}
                  betBuilderIcon={isMarketAvailableForBetBuilder(market.id) ? <BetBuilderIcon firstBColor="#000000" /> : null}
                />
              );
              targetArr.push(betItem);
            } else if (market.id == EnumMarketName.BOTHTEAMSTOSCORE_29) {
              targetArr = bothTeamToScoreTopBets;
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              const betItem = (
                <BetTitleAndBox
                  key={market.stringId}
                  market={market}
                  matchId={providerId}
                  matchDetail={matchDetail}
                  sportType={match.sport}
                  betBox={buttonBg}
                  betBuilderIcon={isMarketAvailableForBetBuilder(market.id) ? <BetBuilderIcon firstBColor="#000000" /> : null}
                />
              );
              targetArr.push(betItem);
            } else {
              targetArr = otherList;
              targetArr.push(
                <div
                  className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
                  key={
                    'd-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2' + market.stringId
                  }>
                  <BetTitleAndBox
                    key={market.stringId}
                    market={market}
                    matchId={providerId}
                    matchDetail={matchDetail}
                    sportType={match.sport}
                    betBox={buttonBg}
                  />
                </div>
              );
            }
          }
        });

        if (winnerTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-winner'}>
              {winnerTopBets}
            </div>
          );
        }

        if (winnerEarlyPayoutBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-winner-early-payout'}>
              {winnerEarlyPayoutBets}
            </div>
          );
        }

        if (nextGoalTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-nextGoal'}>
              {nextGoalTopBets}
            </div>
          );
        }

        if (bothTeamToScoreTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-bothTeamToScore'}>
              {bothTeamToScoreTopBets}
            </div>
          );
        }

        if (overUnderTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-overUnder'}>
              {overUnderTopBets}
            </div>
          );
        }

        if (maradonaHandicapTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-maradona-handicap'}
            >
              <div className="text-start fs-6 d-flex align-items-center mb-1">
                {'Maradona Handicap'}
              </div>
              {maradonaHandicapTopBets}
            </div>
          );
        }

        if (handicapTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-handiCap'}>
              {handicapTopBets}
            </div>
          );
        }

        if (otherList.length > 0) {
          marketList.push(
            <div key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-otherList'}>
              {otherList}
            </div>
          );
        }

        if (goalRangeTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-goalRange'}>
              {goalRangeTopBets}
            </div>
          );
        }

        if (correctScore.length > 0) {
          marketList.push(
            <div key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-correctScore'}>
              {correctScore}
            </div>
          );
        }

        return marketList;
      } else {
        const firstHalfOverUnderList = [];
        const overUnderList = [];
        const firstHalfHandicap = [];
        const handicapList = [];
        const overUnderGamesList = [];
        const gameHandicapList = [];
        const quarterOverUnderList = [];
        const quarterWinner = [];
        const quarterHandicap = [];
        const setHandicapList = [];
        const lastMarkets = [];
        const winnerTopBets = [];
        markets = _.orderBy(markets, (market) => {
          if (
            market.id == EnumMarketName.TOTAL_18 ||
            market.id == EnumMarketName.HOMETOTAL_19 ||
            market.id == EnumMarketName.AWAYTOTAL_20 ||
            market.id == EnumMarketName.FIRSTHALFOVERUNDER_68 ||
            market.id == EnumMarketName.TOTAL_225 ||
            market.id == EnumMarketName.QUARTEROVERUNDER_236 ||
            market.id == EnumMarketName.QUARTERWINNER_235
          ) {
            return market.stringId.split(':')[1];
          } else if (market.id === EnumMarketName.QUARTERHANDICAP_303) {
            return market.stringId.split(':')[2];
          }
        });

        markets.forEach((market) => {
          let targetArr;
          if (market.id != EnumMarketName.ROM_7) {
            let checkArrLength = true;
            let betItem = (
              <BetTitleAndBox
                key={market.stringId}
                market={market}
                matchId={providerId}
                matchDetail={matchDetail}
                sportType={match.sport}
                betBox={buttonBg}
              />
            );
            if (
              market.id == EnumMarketName.TOTAL_18 ||
              market.id == EnumMarketName.TOTAL_225 ||
              market.id == EnumMarketName.TOTALPOINT_238 ||
              market.id == EnumMarketName.TOTAL_258 ||
              market.id == EnumMarketName.TOTALFRAMES_494
            ) {
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
              targetArr = overUnderList;
            } else if (market.id == EnumMarketName.FIRSTHALFHANDICAP_66) {
              targetArr = firstHalfHandicap;
            } else if (market.id == EnumMarketName.WINNER_219) {
              checkArrLength = false;
              targetArr = correctScore;
            } else if (market.id === EnumMarketName.WINNER_186) {
              checkArrLength = false;
              targetArr = winnerTopBets;
            } else if (market.id == EnumMarketName.FIRSTHALFOVERUNDER_68) {
              targetArr = firstHalfOverUnderList;
            } else if (market.id == EnumMarketName.GAMEHANDICAP_187) {
              targetArr = gameHandicapList;
            } else if (market.id == EnumMarketName.TOTALGAMES_189) {
              targetArr = overUnderGamesList;
            } else if (
              market.id == EnumMarketName.HANDICAP_14 ||
              market.id == EnumMarketName.HANDICAP_16 ||
              market.id == EnumMarketName.HANDICAP_223 ||
              market.id == EnumMarketName.HANDICAP_256 ||
              market.id == EnumMarketName.POINTHANDICAP_237 ||
              market.id == EnumMarketName.FRAMEHANDICAP_493
            ) {
              targetArr = handicapList;
            } else if (market.id == EnumMarketName.SETGAMEHANDICAP_203) {
              targetArr = setHandicapList;
            } else if (market.id == EnumMarketName.QUARTERHANDICAP_303) {
              checkArrLength = false;
              targetArr = quarterHandicap;
            } else if (market.id == EnumMarketName.QUARTERWINNER_235) {
              checkArrLength = false;
              targetArr = quarterWinner;
            } else if (market.id == EnumMarketName.QUARTEROVERUNDER_236) {
              targetArr = quarterOverUnderList;
              checkArrLength = false;
            } else {
              checkArrLength = false;
              targetArr = lastMarkets;
              betItem = (
                <div
                  className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
                  key={
                    'd-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-' + market.stringId
                  }>
                  {betItem}
                </div>
              );
            }
            if (checkArrLength && targetArr.length == 0) {
              targetArr.push(
                <div className="text-start fs-6 d-flex align-items-center" key={'bet_name' + market.stringId}>
                  {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                  {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon firstBColor="#000000" />}
                </div>
              );
            }
            targetArr.push(betItem);
          }
        });

        if (winnerTopBets.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-winnerTopBets'}>
              {winnerTopBets}
            </div>
          );
        }
        if (correctScore.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-correctScore'}>
              {correctScore}
            </div>
          );
        if (firstHalfHandicap.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-firstHalfHandicap'}>
              {firstHalfHandicap}
            </div>
          );
        if (handicapList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-handicap'}>
              {handicapList}
            </div>
          );
        if (overUnderList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-overUnder'}>
              {overUnderList}
            </div>
          );

        if (quarterWinner.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-quarterWinner'}>
              {quarterWinner}
            </div>
          );
        }
        if (quarterHandicap.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-quarterHandicap'}>
              {quarterHandicap}
            </div>
          );
        }

        if (quarterOverUnderList.length > 0) {
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-quarterOverUnder'}>
              {quarterOverUnderList}
            </div>
          );
        }
        if (firstHalfOverUnderList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-firstHalfOverUnder'}>
              {firstHalfOverUnderList}
            </div>
          );
        if (overUnderGamesList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-overUnderGames'}>
              {overUnderGamesList}
            </div>
          );
        if (gameHandicapList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-gameHandicap'}>
              {gameHandicapList}
            </div>
          );
        if (setHandicapList.length > 0)
          marketList.push(
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-gameHandicap'}>
              {setHandicapList}
            </div>
          );
        if (lastMarkets.length > 0) marketList.push(lastMarkets);
        return marketList;
      }
    }

    //Second Halftimer Markets Group
    if (betGroupName === EBetGroupName.SECOND_HALF_TIME) {
      const secondHalfOverUnder = [];
      const secondHalfHomeOverUnder = [];
      const secondHalfAwayOverUnder = [];
      markets = _.orderBy(markets, (market) => {
        if (market.id == EnumMarketName.SECONDHALFNEXTGOAL_84) {
          return market.stringId.split(':')[3];
        }
        if (
          market.id == EnumMarketName.SECONDHALFOVERUNDER_90 ||
          market.id == EnumMarketName.SECONDHALFHOMEOVERUNDER_91 ||
          market.id == EnumMarketName.SECONDHALFAWAYOVERUNDER_92
        )
          return market.stringId.split(':')[1];
      });

      markets = sortByMarketId(markets);
      markets = markets.sort((t1, t2) => t1.id - t2.id);
      const marketRom = markets.find((x) => x.id == 83);
      if (marketRom) {
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-' + marketRom.stringId}>
            <BetTitleAndBox
              key={marketRom.stringId}
              market={marketRom}
              matchId={providerId}
              matchDetail={matchDetail}
              sportType={match.sport}
              betBox={buttonBg}
              betBuilderIcon={
                isMarketAvailableForBetBuilder(marketRom.id) ? (
                  <BetBuilderIcon firstBColor="#000000" />
                ) : null
              }
            />
          </div>
        );
      }

      markets.forEach((market) => {
        if (market.id != EnumMarketName.SECONDHALFWINNER_83) {
          const marketId = market.id;
          let targetArr;
          let checkArrLength = true;
          let betItem = (
            <BetTitleAndBox
              key={market.stringId}
              market={market}
              matchId={providerId}
              matchDetail={matchDetail}
              sportType={match.sport}
              betBox={buttonBg}
            />
          );
          if (marketId == EnumMarketName.SECONDHALFOVERUNDER_90) {
            market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
            targetArr = secondHalfOverUnder;
          } else if (marketId == EnumMarketName.SECONDHALFHOMEOVERUNDER_91) {
            targetArr = secondHalfHomeOverUnder;
          } else if (marketId == EnumMarketName.SECONDHALFAWAYOVERUNDER_92) {
            targetArr = secondHalfAwayOverUnder;
          } else {
            if (
              marketId == EnumMarketName.SECONDHALFNEXTGOAL_84 ||
              marketId == EnumMarketName.SECONDHALFWINNER_83
            ) {
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
            }
            checkArrLength = false;
            targetArr = marketList;
            betItem = (
              <div
                className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
                key={
                  'd-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-' + market.stringId
                }>
                {betItem}
              </div>
            );
          }
          if (checkArrLength && targetArr.length == 0) {
            targetArr.push(
              <div className="text-start fs-6 d-flex align-items-center" key={'bet_name' + market.stringId}>
                {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon firstBColor="#000000" />}
              </div>
            );
          }
          targetArr.push(betItem);
        }
      });

      if (secondHalfOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-secondHalfOverUnder'}>
            {secondHalfOverUnder}
          </div>
        );
      if (secondHalfHomeOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-secondHalfHomeOverUnder'}>
            {secondHalfHomeOverUnder}
          </div>
        );
      if (secondHalfAwayOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-secondtHalfAwayOverUnder'}>
            {secondHalfAwayOverUnder}
          </div>
        );
      return marketList;
    }

    // Over Under Markets Groups
    if (betGroupName == EBetGroupName.Over_Under) {
      const overUnderList = [];
      const homeOverUnderList = [];
      const awayOverUnderList = [];
      const firstHalfOverUnderList = [];
      const overUnderGamesList = [];
      const otherList = [];
      const firstHalfAwayOverUnderList = [];
      const firstHalfHomeOverUnderList = [];
      const secondHalfOverUnderList = [];
      const secondHalfHomeOverUnderList = [];
      const secondHalfAwayOverUnderList = [];
      const homeAnotherUnderList = [];
      const awayAnotherUnderList = [];
      const secondHalfTotallist = [];
      markets = _.orderBy(markets, (market) => {
        if (
          market.id == EnumMarketName.TOTAL_18 ||
          market.id == EnumMarketName.HOMETOTAL_19 ||
          market.id == EnumMarketName.AWAYTOTAL_20 ||
          market.id == EnumMarketName.FIRSTHALFOVERUNDER_68 ||
          market.id == EnumMarketName.TOTAL_225 ||
          market.id == EnumMarketName.FIRSTHALFHOMEOVERUNDER_69 ||
          market.id == EnumMarketName.FIRSTHALFAWAYOVERUNDER_70 ||
          market.id == EnumMarketName.SECONDHALFOVERUNDER_90 ||
          market.id == EnumMarketName.SECONDHALFHOMEOVERUNDER_91 ||
          market.id == EnumMarketName.SECONDHALFAWAYOVERUNDER_92 ||
          market.id == EnumMarketName.HOMEOVERUNDER_227 ||
          market.id == EnumMarketName.AWAYOVERUNDER_228 ||
          market.id == EnumMarketName.SECONDHALFTOTAL_232 ||
          market.id == EnumMarketName.HOMEOVERUNDER_756 ||
          market.id == EnumMarketName.AWAYOVERUNDER_757
        )
          return market.stringId.split(':')[1];
      });
      markets.forEach((market) => {
        const marketId = market.id;
        let targetArr;
        let checkArrLength = true;
        let betItem = (
          <BetTitleAndBox
            key={market.stringId}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
            betBox={buttonBg}
          />
        );
        if (
          marketId == EnumMarketName.TOTAL_18 ||
          marketId == EnumMarketName.TOTAL_225 ||
          marketId == EnumMarketName.TOTALPOINT_238 ||
          marketId == EnumMarketName.TOTAL_258 ||
          marketId == EnumMarketName.TOTALFRAMES_494
        ) {
          market.outcomes = _.orderBy(market?.outcomes, ['id'], ['asc']);
          targetArr = overUnderList;
        } else if (
          marketId == EnumMarketName.HOMETOTAL_19 ||
          marketId === EnumMarketName.HOMEOVERUNDER_756
        ) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = homeOverUnderList;
        } else if (
          marketId == EnumMarketName.AWAYTOTAL_20 ||
          marketId === EnumMarketName.AWAYOVERUNDER_757
        ) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = awayOverUnderList;
        } else if (marketId == EnumMarketName.FIRSTHALFOVERUNDER_68) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = firstHalfOverUnderList;
        } else if (marketId == EnumMarketName.FIRSTHALFHOMEOVERUNDER_69) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = firstHalfHomeOverUnderList;
        } else if (marketId == EnumMarketName.FIRSTHALFAWAYOVERUNDER_70) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = firstHalfAwayOverUnderList;
        } else if (marketId == EnumMarketName.SECONDHALFOVERUNDER_90) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = secondHalfOverUnderList;
        } else if (marketId == EnumMarketName.SECONDHALFTOTAL_232) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = secondHalfTotallist;
        } else if (marketId == EnumMarketName.SECONDHALFHOMEOVERUNDER_91) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = secondHalfHomeOverUnderList;
        } else if (marketId == EnumMarketName.SECONDHALFAWAYOVERUNDER_92) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = secondHalfAwayOverUnderList;
        } else if (marketId == EnumMarketName.TOTALGAMES_189) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = overUnderGamesList;
        } else if (marketId == EnumMarketName.HOMEOVERUNDER_227) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = homeAnotherUnderList;
        } else if (marketId == EnumMarketName.AWAYOVERUNDER_228) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = awayAnotherUnderList;
        } else {
          targetArr = otherList;
          checkArrLength = false;
          betItem = (
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-' + market.stringId}>
              {betItem}
            </div>
          );
        }

        if (checkArrLength && targetArr.length == 0) {
          targetArr.push(
            <div className="text-start fs-6 d-flex align-items-center" key={'bet_name' + market.stringId}>
              {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
              {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon />}
            </div>
          );
        }

        targetArr.push(betItem);
      });

      if (overUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-overUnder'}>
            {overUnderList}
          </div>
        );
      if (homeOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-homeOverUnder'}>
            {homeOverUnderList}
          </div>
        );
      if (awayOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-awayOverUnder'}>
            {awayOverUnderList}
          </div>
        );

      if (homeAnotherUnderList.length > 0) {
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-homeAnotherUnder'}>
            {homeAnotherUnderList}
          </div>
        );
      }

      if (awayAnotherUnderList.length > 0) {
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-awayAnotherUnder'}>
            {awayAnotherUnderList}
          </div>
        );
      }
      if (firstHalfOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-firstHalfOverUnder'}>
            {firstHalfOverUnderList}
          </div>
        );
      if (firstHalfAwayOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-firstHalfAwayOverUnderList'}>
            {firstHalfAwayOverUnderList}
          </div>
        );

      if (firstHalfHomeOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-firstHalfHomeOverUnderList'}>
            {firstHalfHomeOverUnderList}
          </div>
        );
      if (secondHalfOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-secondHalfOverUnderList'}>
            {secondHalfOverUnderList}
          </div>
        );
      if (secondHalfHomeOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-secondHalfHomeOverUnderList'}>
            {secondHalfHomeOverUnderList}
          </div>
        );
      if (secondHalfAwayOverUnderList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-secondHalfAwayOverUnderList'}>
            {secondHalfAwayOverUnderList}
          </div>
        );
      if (secondHalfTotallist.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-secondHalfTotallist'}>
            {secondHalfTotallist}
          </div>
        );
      if (overUnderGamesList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-overUnderGames'}>
            {overUnderGamesList}
          </div>
        );
      if (otherList.length > 0) marketList.push(otherList);

      return marketList;
    }
    // 1st Halftime Markets Group
    if (betGroupName == EBetGroupName.FIRST_HALF_TIME) {
      const firstHalfHandicap = [];
      const firstHalfOverUnder = [];
      const firstHalfHomeOverUnder = [];
      const firstHalfAwayOverUnder = [];
      const firstHalfExactGoals = [];
      const multiGoals = [];
      const firstBothTeamsToScore = [];
      const firstHalfOtherHandicap = [];
      markets = _.orderBy(markets, (market) => {
        if (market.id == EnumMarketName.FIRSTHALFEXACTGOALS_71) {
          return market.stringId.split(':')[3];
        }
        if (
          market.id == EnumMarketName.FIRSTHALFOVERUNDER_68 ||
          market.id == EnumMarketName.FIRSTHALFHOMEOVERUNDER_69 ||
          market.id == EnumMarketName.FIRSTHALFAWAYOVERUNDER_70
        )
          return market.stringId.split(':')[1];
      });

      markets = sortByMarketId(markets);
      markets = markets.sort((t1, t2) => t1.id - t2.id);
      const marketRom = markets.find((x) => x.id == 61);
      if (marketRom) {
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-' + marketRom.stringId}>
            <BetTitleAndBox
              key={marketRom.stringId}
              market={marketRom}
              matchId={providerId}
              matchDetail={matchDetail}
              sportType={match.sport}
              betBox={buttonBg}
              betBuilderIcon={
                isMarketAvailableForBetBuilder(marketRom.id) ? (
                  <BetBuilderIcon firstBColor="#000000" />
                ) : null
              }
            />
          </div>
        );
      }

      markets.forEach((market) => {
        if (market.id != EnumMarketName.FIRSTHALFROM_61) {
          const marketId = market.id;
          let targetArr;
          let checkArrLength = true;
          let betItem = (
            <BetTitleAndBox
              key={market.stringId}
              market={market}
              matchId={providerId}
              matchDetail={matchDetail}
              sportType={match.sport}
              betBox={buttonBg}
            />
          );
          if (marketId == EnumMarketName.FIRSTHALFHANDICAP_65) {
            targetArr = firstHalfHandicap;
          } else if (marketId == EnumMarketName.FIRSTHALFOVERUNDER_68) {
            market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
            targetArr = firstHalfOverUnder;
          } else if (marketId == EnumMarketName.FIRSTHALFHOMEOVERUNDER_69) {
            targetArr = firstHalfHomeOverUnder;
          } else if (marketId == EnumMarketName.FIRSTHALFAWAYOVERUNDER_70) {
            targetArr = firstHalfAwayOverUnder;
          } else if (marketId == EnumMarketName.FIRSTHALFEXACTGOALS_71) {
            targetArr = firstHalfExactGoals;
          } else if (marketId == EnumMarketName.FIRSTHALFMULTIGOALS_552) {
            targetArr = multiGoals;
          } else if (marketId == EnumMarketName.FIRSTHALFBOTHTEAMSTOSCORE_75) {
            targetArr = firstBothTeamsToScore;
          } else if (marketId == EnumMarketName.FIRSTHALFHANDICAP_66) {
            targetArr = firstHalfOtherHandicap;
          } else {
            if (
              marketId == EnumMarketName.FIRSTHALFNEXTGOAL_62 ||
              marketId == EnumMarketName.FIRSTHALFROM_61
            ) {
              market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
            }
            checkArrLength = false;
            targetArr = marketList;
            betItem = (
              <div
                className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
                key={
                  'd-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-' + market.stringId
                }>
                {betItem}
              </div>
            );
          }
          const firstHalfSkipHeaderIds = [
            EnumMarketName.FIRSTHALFEXACTGOALS_71,
            EnumMarketName.FIRSTHALFBOTHTEAMSTOSCORE_75,
            EnumMarketName.FIRSTHALFMULTIGOALS_552
          ];
          if (
            checkArrLength &&
            targetArr.length == 0 &&
            !firstHalfSkipHeaderIds.includes(marketId)
          ) {
            targetArr.push(
              <div className="text-start fs-6 d-flex align-items-center" key={'bet_name' + market.stringId}>
                {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
                {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon firstBColor="#000000" />}
              </div>
            );
          }
          targetArr.push(betItem);
        }
      });

      if (firstHalfHandicap.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-firstHalfHandicap'}>
            {firstHalfHandicap}
          </div>
        );
      if (firstHalfOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-firstHalfOverUnder'}>
            {firstHalfOverUnder}
          </div>
        );
      if (firstHalfOtherHandicap.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-firstHalfHandicap'}>
            {firstHalfOtherHandicap}
          </div>
        );
      if (firstHalfHomeOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-firstHalfHomeOverUnder'}>
            {firstHalfHomeOverUnder}
          </div>
        );
      if (firstHalfAwayOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-firstHalfAwayOverUnder'}>
            {firstHalfAwayOverUnder}
          </div>
        );
      if (firstHalfExactGoals.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-firstHalfExactGoals'}>
            {firstHalfExactGoals}
          </div>
        );
      if (firstBothTeamsToScore.length > 0) {
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'market-list-firstBothTeamsToScore'}>
            {firstBothTeamsToScore}
          </div>
        );
      }
      if (multiGoals.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}>
            {multiGoals}
          </div>
        );
      return marketList;
    }
    // Handicap Markets Group
    if (betGroupName == EBetGroupName.HANDICAP) {
      const handicapList = [];
      const maradonaHandicapList = [];
      const firstHalfHandicap = [];
      const gameHandicap = [];
      const quarterHandicap = [];
      const setHandicapList = [];
      const secondHalfHandicapList = [];
      markets = sortByMarketId(markets);
      markets.forEach((market) => {
        const marketId = market.id;
        let targetArr;
        let checkArrLength = true;
        let betItem = (
          <BetTitleAndBox
            key={market.stringId}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
            betBox={buttonBg}
          />
        );

       
        const specifier = checkHasMarketVariable(marketId, market.stringId);
        const isMaradonaHandicap =
          marketId == EnumMarketName.HANDICAP_14 &&
          match.eventStatus === EnumEventStatus.NOTSTARTED &&
          (specifier === '0:9' || specifier === '9:0');

        if (isMaradonaHandicap) {
          
          checkArrLength = false;
          targetArr = maradonaHandicapList;
        } else if (
          marketId == EnumMarketName.HANDICAP_14 ||
          marketId == EnumMarketName.HANDICAP_16 ||
          marketId == EnumMarketName.GAMEHANDICAP_187 ||
          marketId == EnumMarketName.HANDICAP_223 ||
          marketId == EnumMarketName.HANDICAP_256 ||
          marketId == EnumMarketName.POINTHANDICAP_237 ||
          marketId == EnumMarketName.FRAMEHANDICAP_493
        ) {
          targetArr = handicapList;
        } else if (
          marketId == EnumMarketName.FIRSTHALFHANDICAP_65 ||
          marketId == EnumMarketName.FIRSTHALFHANDICAP_66
        ) {
          targetArr = firstHalfHandicap;
        } else if (market.id == EnumMarketName.SETGAMEHANDICAP_203) {
          targetArr = setHandicapList;
        } else if (marketId == EnumMarketName.gameHandicap) {
          targetArr = gameHandicap;
        } else if (marketId == EnumMarketName.SECONDHALFHANDICAP_231) {
          targetArr = secondHalfHandicapList;
        } else if (marketId == EnumMarketName.QUARTERHANDICAP_303) {
          checkArrLength = false;
          targetArr = quarterHandicap;
        } else {
          checkArrLength = false;
          targetArr = marketList;
          betItem = (
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-' + market.stringId}>
              {betItem}
            </div>
          );
        }

        if (checkArrLength && targetArr.length == 0) {
          targetArr.push(
            <div className="text-start fs-6 d-flex align-items-center" key={'bet_name' + market.stringId}>
              {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
              {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon />}
            </div>
          );
        }
        targetArr.push(betItem);
      });

      if (maradonaHandicapList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-maradona-handicap'}
          >
            <div className="text-start fs-6 d-flex align-items-center" key="maradona-handicap-header">
              {'Maradona Handicap'}
            </div>
            {maradonaHandicapList}
          </div>
        );

      if (handicapList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-handicap'}>
            {handicapList}
          </div>
        );
      if (firstHalfHandicap.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-firstHalfHandicap'}>
            {firstHalfHandicap}
          </div>
        );
      if (setHandicapList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-gameHandicap'}>
            {setHandicapList}
          </div>
        );
      if (quarterHandicap.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-firstHalfHandicap'}>
            {quarterHandicap}
          </div>
        );
      if (gameHandicap.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-gameHandicap'}>
            {gameHandicap}
          </div>
        );
      if (secondHalfHandicapList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={
              'd-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-secondHalfHandicapList'
            }>
            {secondHalfHandicapList}
          </div>
        );
      return marketList;
    }
    // Corner Markets group
    if (betGroupName === EBetGroupName.CORNER) {
      const cornerOverUnder = [];
      markets = sortByMarketId(markets);

      markets.forEach((market, index) => {
        const marketId = market.id;
        let targetArr;
        let checkArrLength = true;
        let betItem = (
          <BetTitleAndBox
            key={index + market.stringId}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
            betBox={buttonBg}
          />
        );
        if (marketId == 166) {
          targetArr = cornerOverUnder;
        } else {
          targetArr = marketList;
          checkArrLength = false;
          betItem = (
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={
                'd-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-' +
                market.stringId +
                index
              }>
              {betItem}
            </div>
          );
        }
        if (checkArrLength && targetArr.length == 0) {
          targetArr.push(
            <div className="text-start fs-6 d-flex align-items-center" key={'bet_name' + market.stringId + index}>
              {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
              {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon />}
            </div>
          );
        }
        targetArr.push(betItem);
      });
      if (cornerOverUnder.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-cornerOverUnder'}>
            {cornerOverUnder}
          </div>
        );
      return marketList;
    }
    // Goals Markets Group
    if (betGroupName === EBetGroupName.GOALS) {
      const exactGoals = [];
      const goalRange = [];
      const multiScores = [];
      markets = _.orderBy(markets, (market) => {
        if (market.id == EnumMarketName.EXACTGOALS_21 || market.id == EnumMarketName.GOALRANGE_25) {
          return market.stringId.split(':')[3];
        }
      });

      //Remove other next goal markets
      // let nextGoalMinBySpecifier = { market: null, specifier: 9999 };

      // markets.forEach((market) => {
      //   const specifier = Number(market.stringId.split(':')[1] ?? -1);
      //   if (specifier > -1) {
      //     if (market.id == 8 && specifier < nextGoalMinBySpecifier.specifier) {
      //       nextGoalMinBySpecifier = { market, specifier };
      //     }
      //   }
      // });

      // if (nextGoalMinBySpecifier.market) {
      //   markets = markets.filter((m) => m.id !== 8);
      //   markets.push(nextGoalMinBySpecifier.market);
      // }
      // Remove other next goal markets end

      markets.forEach((market, index) => {
        const marketId = market.id;
        let targetArr;
        let checkArrLength = true;
        market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
        let betItem = (
          <BetTitleAndBox
            key={'group_6' + market.stringId + index}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
            betBox={buttonBg}
          />
        );
        if (marketId == EnumMarketName.EXACTGOALS_21) {
          targetArr = exactGoals;
        } else if (marketId == EnumMarketName.GOALRANGE_25) {
          market.outcomes = _.orderBy(market.outcomes, ['id'], ['asc']);
          targetArr = goalRange;
        } else if (marketId == EnumMarketName.MULTISCORES_551) {
          targetArr = multiScores;
        } else {
          checkArrLength = false;
          targetArr = marketList;
          betItem = (
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-' + market.stringId}>
              {betItem}
            </div>
          );
        }
        if (
          checkArrLength &&
          targetArr.length == 0 &&
          marketId != EnumMarketName.MULTISCORES_551
        ) {
          targetArr.push(
            <div className="text-start fs-6 d-flex align-items-center" key={'bet_name' + market.stringId}>
              {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
              {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon />}
            </div>
          );
        }
        targetArr.push(betItem);
      });

      if (exactGoals.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-exactGoals'}>
            {exactGoals}
          </div>
        );
      if (goalRange.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-goalRange'}>
            {goalRange}
          </div>
        );
      if (multiScores.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-multiScores'}>
            {multiScores}
          </div>
        );
      return marketList;
    }
    // Double Markets Group
    if (betGroupName === EBetGroupName.DOUBLE_BETS) {
      const winnerOverUnderList = [];
      const DoubleChanceAndOverUnder = [];

      markets = sortByMarketId(markets);
      markets.forEach((market, index) => {
        const marketId = market.id;
        let targetArr;
        let checkArrLength = true;
        let betItem = (
          <BetTitleAndBox
            key={'group_8' + market.stringId + index}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
            betBox={buttonBg}
          />
        );
        if (marketId == EnumMarketName.WINNERANDOVERUNDER_37) {
          targetArr = winnerOverUnderList;
        } else if (marketId == EnumMarketName.DOUBLECHANCETOTAL_547) {
          targetArr = DoubleChanceAndOverUnder;
        } else {
          checkArrLength = false;
          targetArr = marketList;
          betItem = (
            <div
              className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
              key={
                'd-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2' +
                market.stringId +
                index
              }>
              {betItem}
            </div>
          );
        }
        if (checkArrLength && targetArr.length == 0) {
          targetArr.push(
            <div className="text-start fs-6 d-flex align-items-center" key={'bet_name' + market.stringId + index}>
              {MarketIdToMarketName(market.id, market.stringId).split(' + ')[0]}
              {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon />}
            </div>
          );
        }
        if (checkArrLength) {
          targetArr.push(betItem, <hr></hr>);
        } else {
          targetArr.push(betItem);
        }
      });
      if (winnerOverUnderList.length > 0) {
        winnerOverUnderList.pop();
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-winneroverunder'}>
            {winnerOverUnderList}
          </div>
        );
      }
      if (DoubleChanceAndOverUnder.length > 0) {
        DoubleChanceAndOverUnder.pop();
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
            key={'d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2-doublechance'}>
            {DoubleChanceAndOverUnder}
          </div>
        );
      }
      return marketList;
    }
    // Player Markets Group
    if (betGroupName === EBetGroupName.PLAYER) {
      const goalScorerList = [];
      const anytimeGoalScorer = [];
      const anytimeWinner = [];
      const otherList = [];
      markets.forEach((market, index) => {
        const marketId = market.id;
        let targetArr;
        if (
          marketId == EnumMarketName.NEXTGOALSCORER_38 ||
          marketId == EnumMarketName.LASTGOALSCORER_39 ||
          marketId == EnumMarketName.ANYTIMEGOALSCORER_40
        ) {
          if (goalScorerList.length == 0) {
            goalScorerList.push(
              <div key={'footballer-name' + index}>
                <Row className="text-start fs-6">
                  <Col xs={6}>{t('goalScorer')}</Col>
                </Row>
                <Row>
                  <Col xs={6}></Col>
                  <Col xs={2} className="fs-8">
                    {t('oddType.first')}
                  </Col>
                  <Col xs={2} className="ps-0 pe-3 fs-8">
                    {t('oddType.anytime')}
                  </Col>
                  <Col xs={2} className="ps-2 pe-5 fs-8">
                    {t('oddType.last')}
                  </Col>
                </Row>
                {_.orderBy(market.outcomes, ['odds'], ['asc']).map((outcome) => (
                  <>
                    {outcome.odds == 0 || outcome.odds == 1.0 ? null : (
                      <Container>
                        <Row className="mb-1">
                          <Col xs={6} className="text-start fs-6">
                            {ReverseName(outcome.name)}
                          </Col>
                          {getGoalScorers(outcome.id, EnumMarketName.NEXTGOALSCORER_38)}
                          {getGoalScorers(outcome.id, EnumMarketName.ANYTIMEGOALSCORER_40)}
                          {getGoalScorers(outcome.id, EnumMarketName.LASTGOALSCORER_39)}
                        </Row>
                      </Container>
                    )}
                  </>
                ))}
              </div>
            );
          }
        } else if (
          marketId == EnumMarketName.ANYTIMEGOALSCORERWINNER_888 ||
          marketId == EnumMarketName.NEXTGOALSCORERWINNER_891
        ) {
          targetArr =
            marketId == EnumMarketName.ANYTIMEGOALSCORERWINNER_888
              ? anytimeGoalScorer
              : anytimeWinner;

          targetArr.push(
            <div key={'any-time-' + marketId + '' + index}>
              {targetArr.length === 0 && (
                <div className="text-start fs-6 d-flex align-items-center">
                  {MarketIdToMarketName(market.id, market.stringId)}
                  {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon firstBColor="#000000" />}
                </div>
              )}
              <BetTitleAndBox
                key={market.stringId}
                market={market}
                matchId={providerId}
                matchDetail={matchDetail}
                sportType={match.sport}
                betBox={buttonBg}
              />
            </div>
          );
        } else {
          otherList.push(
            <div key={'other-' + marketId + '' + index}>
              <Row className="text-start fs-6 d-flex align-items-center">
                <span>{MarketIdToMarketName(market.id, market.stringId)}</span>
                {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon firstBColor="#000000" />}
              </Row>
              <BetTitleAndBox
                key={market.stringId}
                market={market}
                matchId={providerId}
                matchDetail={matchDetail}
                sportType={match.sport}
                betBox={buttonBg}
              />
            </div>
          );
        }
      });
      if (goalScorerList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}>
            {goalScorerList}
          </div>
        );
      if (anytimeGoalScorer.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}>
            {anytimeGoalScorer}
          </div>
        );
      if (anytimeWinner.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}>
            {anytimeWinner}
          </div>
        );
      if (otherList.length > 0)
        marketList.push(
          <div
            className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}>
            {otherList}
          </div>
        );
      return marketList;
    }

    // if groups not matches any market do this//
    if (betGroupName == EBetGroupName.TRIPLE_BETS) {
      markets = markets.sort(
        (t1, t2) => Number(t1.stringId.split(':')[1]) - Number(t2.stringId.split(':')[1])
      );
    }
    markets.forEach((market) => {
      const showOuterHeader = market.id == EnumMarketName.HALFTIMEFULLTIMEEXACTGOALS_820;
      marketList.push(
        <div
          className={`d-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2 ${detailBorderClass}`}
          key={
            'd-flex flex-column gap-2 border-gray-300 rounded p-2 mb-2' + market.stringId
          }>
          {showOuterHeader && (
            <div className="text-start fs-6 d-flex align-items-center mb-2">
              {MarketIdToMarketName(market.id, market.stringId)}
              {isMarketAvailableForBetBuilder(market.id) && <BetBuilderIcon />}
            </div>
          )}
          <BetTitleAndBox
            key={market.stringId}
            market={market}
            matchId={providerId}
            matchDetail={matchDetail}
            sportType={match.sport}
            betBox={buttonBg}
          />
        </div>
      );
    });
    return marketList;
  }

  function PeriodScoreView() {
    let accumulatedHomeScore = 0;
    let accumulatedAwayScore = 0;
    return match?.periodScores?.map((periodScore, index) => {
      // Ignore for tennis and volleyball
      if (
        match.sport !== sportTypeToSportId('Tennis') &&
        match.sport !== sportTypeToSportId('Volleyball') &&
        index + 1 >= match.periodScores.length
      ) {
        return null;
      }
      if (match.sport == sportTypeToSportId('Basketball')) {
        // Basketball
        accumulatedHomeScore += periodScore.homeScore;
        accumulatedAwayScore += periodScore.awayScore;
        return <div key={index}>{`(${accumulatedHomeScore}:${accumulatedAwayScore})`}</div>;
      }
      return <div key={index}>{`(${periodScore.homeScore}:${periodScore.awayScore})`}</div>;
    });
  }

  if (match === undefined || matchDetail === undefined) return <div />;

  const detailClass =
    match.matchStatusId === EnumMatchStatusName.HALFTIME
      ? 'match-halftime'
      : match.eventStatus === EnumEventStatus.RUNNING
        ? 'match-live'
        : 'match-not-started';

  return (
    <>
      <div
        className="matchdetail_header"
        style={{
          backgroundImage: `url(${AWS_BUCKET_URL}/Icons/122.png)`,
          backgroundSize: 'cover',
          height: 190
        }}>
        <Row>
          <div className="headerMatchInfo">
            <Row>
              <Col xs={10}>
                <div className="d-flex flex-row">
                  <GoBack
                    location={location.path}
                    selectSport={location.sport}
                    position={'detail'}
                  />
                  <div className="ml-2">
                    <div className="fsize-13">
                      {match.eventStatus > EnumEventStatus.NOTSTARTED ? (
                        <Badge pill bg="danger">
                          {t('components.matchcard.live')}
                        </Badge>
                      ) : (
                        <div className="fsize-10">
                          {gameDateToMatchDayFormatted(match.gameDate, getLanguageCode())}{' '}
                        </div>
                      )}
                    </div>
                    <div className="d-flex flex-row">
                      <div className="pt-1 mr-2 fsize-13">{match.league}</div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <div className="stats"></div>
          </div>
        </Row>
        <Row>
          <Col xs={4}>
            <div className="text-white text-center fs-6 ms-1 d-flex flex-column align-items-center">
              <Jersey jersey={matchDetail.home.jersey} color={'white'} />

              {getTeamNameHelper(locales, selectedLocale, matchDetail.home, t)}
            </div>
          </Col>
          <Col xs={4}>
            <div className="m-auto text-white border border-2 border-info rounded d-flex justify-content-center">
              {match.eventStatus > EnumEventStatus.NOTSTARTED ? (
                getMatchTime(match, t)
              ) : (
                <div className="text-capitalize fs-9">
                  {t(`matchStatus.${match.matchStatusId}`)}
                </div>
              )}
            </div>{' '}
            <div className="text-center text-light fs-9 mt-2 h-0 overflow-visible">
              <PeriodScoreView />
            </div>
          </Col>
          <Col xs={4}>
            <div className="text-white text-center fs-6 me-1 d-flex flex-column align-items-center">
              <Jersey jersey={matchDetail.away.jersey} color={'black'} />

              {getTeamNameHelper(locales, selectedLocale, matchDetail.away, t)}
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="team1 mt-2">{matchDetail.home.homeScore}</div>
          </Col>
          <Col></Col>
          <Col>
            <div className="team1  mt-2">{matchDetail.away.awayScore}</div>
          </Col>
        </Row>
      </div>
      <div>
        <div className="d-flex" style={{ backgroundColor: 'black' }}>
          <div
            className="flex-fill d-flex flex-column align-items-center justify-content-center position-relative"
            onClick={() => {
              // Tracker tab - always show tracker with default tab
              setCurrentView('tracker');
              setTrackerTab('tracker');
              setIsStreamView(false);
              setStreamError(false);
            }}
            style={{
              cursor: 'pointer',
              backgroundColor:
                currentView === 'tracker' && trackerTab === 'tracker'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'transparent',
              borderRight: streamAvailable ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
            }}>
            <div className="mb-1">
              {/* SVG Icon 1 - Tracker */}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <rect
                  x="3"
                  y="3"
                  width="18"
                  height="18"
                  rx="2"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                />
                <line x1="9" y1="3" x2="9" y2="21" stroke="white" strokeWidth="2" />
                <line x1="15" y1="3" x2="15" y2="21" stroke="white" strokeWidth="2" />
                <line x1="3" y1="9" x2="21" y2="9" stroke="white" strokeWidth="2" />
                <line x1="3" y1="15" x2="21" y2="15" stroke="white" strokeWidth="2" />
              </svg>
            </div>
            <div className="text-white fs-8">{t('tracker')}</div>
          </div>

          <div
            className="flex-fill d-flex flex-column align-items-center justify-content-center position-relative"
            onClick={() => {
              // Statistics tab - show statistics component directly
              setCurrentView('statistics');
              setIsStreamView(false);
              setStreamError(false);
            }}
            style={{
              cursor: 'pointer',
              backgroundColor:
                currentView === 'statistics' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              borderRight: streamAvailable ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
            }}>
            <div className="mb-1">
              {/* SVG Icon 2 - Statistics */}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="17" width="4" height="4" fill="white" />
                <rect x="10" y="13" width="4" height="8" fill="white" />
                <rect x="17" y="9" width="4" height="12" fill="white" />
              </svg>
            </div>
            <div className="text-white fs-8">{t('statistics')}</div>
          </div>

          {isBetBuilderAvailable() && (
            <div
              className="flex-fill d-flex flex-column align-items-center justify-content-center position-relative"
              onClick={() => {
                // Bet Builder tab - toggle bet builder mode
                const willEnable = !betBuilder.isEnabled;
                if (willEnable) {
                  // Check if there are normal bets for this event before enabling bet builder
                  const hasNormalBet = betSlip.bets.some(
                    (b) => b.providerId === providerId && !b.isBetBuilder
                  );
                  if (hasNormalBet) {
                    showToaster('If custom bet is added for sport event, you cannot add normal market', 'error');
                    return;
                  }
                }
                dispatch(setBetBuilderEnabled(willEnable));
                setCurrentView('betBuilder');
                setIsStreamView(false);
                setStreamError(false);
              }}
              style={{
                cursor: 'pointer',
                backgroundColor: betBuilder.isEnabled ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                borderRight: streamAvailable ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
              }}
            >
              <div className="mb-1">
                <BetBuilderIcon size={18} style={{ marginLeft: '0' }} firstBColor="#FFFFFF" />
              </div>
              <div className="text-white fs-8">{t('betBuilder.title')}</div>
            </div>
          )}

          {streamAvailable && (
            <div
              className="flex-fill d-flex flex-column align-items-center justify-content-center position-relative"
              onClick={() => {
                // Show stream in iframe (either fullscreen URL or regular URL)
                setCurrentView('stream');
                setIsStreamView(true);
              }}
              style={{
                cursor: 'pointer',
                backgroundColor:
                  currentView === 'stream' ? 'rgba(255, 255, 255, 0.1)' : 'transparent'
              }}>
              <div className="mb-1 position-relative">
                {/* SVG Icon 3 - Live TV */}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <rect
                    x="2"
                    y="3"
                    width="20"
                    height="14"
                    rx="2"
                    stroke="white"
                    strokeWidth="2"
                    fill="none"
                  />
                  <polyline points="22,17 12,21 2,17" stroke="white" strokeWidth="2" fill="none" />
                  <polygon points="10,8 14,10 10,12" fill="white" />
                </svg>
                {/* Live indicator dot */}
                <div
                  className="position-absolute"
                  style={{
                    top: '-2px',
                    right: '-2px',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#ff4444',
                    borderRadius: '50%',
                    border: '1px solid white'
                  }}
                />
              </div>
              <div className="text-white fs-8">{t('liveTv')}</div>
            </div>
          )}
        </div>

        {currentView === 'tracker' ? (
          <MatchTracker
            matchId={match.providerId.split(':')[2]}
            language={getLanguageCode().split('-')[0]}
            isDesktop={!!matchDetailId}
            selectedTabCallbackFunc={setMatchtrackerCallback}
            defaultTab={trackerTab}
            onTabChange={(tabName) => {
              setTrackerTab(tabName);
            }}
          />
        ) : currentView === 'statistics' ? (
          <Statistics
            matchId={match.providerId.split(':')[2]}
            language={getLanguageCode().split('-')[0]}
            isDesktop={!!matchDetailId}
          />
        ) : currentView === 'stream' && (streamUrl || fullscreenUrl) ? (
          <MatchStreamer
            matchId={match.providerId.split(':')[2]}
            streamUrl={streamUrl}
            fullscreenUrl={fullscreenUrl}
            isVisible={currentView === 'stream'}
          />
        ) : null}
      </div>
      <div className="scrollmenuFullWidth blackBgColor">
        {betBuilder.isEnabled && isBetBuilderAvailable() && (
          <div className="bet-builder-indicator">
            <span className="badge bg-success me-2">
              <i className="fas fa-puzzle-piece me-1"></i>
              {t('betBuilderMode')}
            </span>
          </div>
        )}
        {availableMarketGroups &&
          availableMarketGroups.map((market) => {
            return (
              <div
                key={market}
                className={`sport-item ${selectedMarket === market ? ' green-underline' : ''}`}
                onClick={() => setSelectedMarket(market)}>
                {t(`betGroup.${market}`)}
              </div>
            );
          })}
      </div>

      <div className={`container-fluid pe-1 ps-1 match-details ${detailClass}`}>
        {availableMarketGroups && getMarkets(selectedMarket)}
      </div>
      
      
      {isBetBuilderAvailable() && <BetBuilderModal />}
    </>
  );
}
export default MatchDetail;
